<script setup lang="ts">
import { useRamadanTime } from "@/composables/useRamadanTime";
import { computed } from "vue";

const STAR_COUNT = 50;
const LANTERN_COUNT = 8;
const PARTICLE_COUNT = 30;

function seededValue(seed: number) {
  const value = Math.sin(seed * 9999) * 10000;
  return value - Math.floor(value);
}

function createStyles(
  count: number,
  factory: (index: number) => Record<string, string>,
) {
  return Array.from({ length: count }, (_, index) => ({
    key: index + 1,
    style: factory(index + 1),
  }));
}

function formatOrdinal(value: number) {
  const mod10 = value % 10;
  const mod100 = value % 100;
  if (mod10 === 1 && mod100 !== 11) return `${value}st`;
  if (mod10 === 2 && mod100 !== 12) return `${value}nd`;
  if (mod10 === 3 && mod100 !== 13) return `${value}rd`;
  return `${value}th`;
}

const { today } = useRamadanTime();

const shawwalDay = computed(() => {
  const match = today.value?.hijriDate?.match(/^(\d+)\s+Shawwal\b/i);
  if (!match) return 1;
  const parsed = Number.parseInt(match[1] ?? "", 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
});

const isFirstDayOfEid = computed(() => shawwalDay.value === 1);
const dayOrdinal = computed(() => formatOrdinal(shawwalDay.value));

const pageTitle = computed(() =>
  isFirstDayOfEid.value
    ? "Eid Mubarak Pakistan"
    : `${dayOrdinal.value} Day of Eid - Pakistan`,
);

const pageDescription = computed(() =>
  isFirstDayOfEid.value
    ? "Rozay.today ki taraf se Eid Mubarak. Apne ghar walon aur doston ke saath is khushi ke din ko dua, mohabbat aur shukr ke saath manayein."
    : `Aaj Pakistan mein ${shawwalDay.value} Shawwal hai, yani Eid ka ${dayOrdinal.value} din. Ghar walon ke saath is mubarak mauqe ko dua aur shukr ke saath manayein.`,
);

const heroTitleTop = computed(() =>
  isFirstDayOfEid.value ? "Eid" : `${dayOrdinal.value} Day`,
);
const heroTitleBottom = computed(() =>
  isFirstDayOfEid.value ? "Mubarak" : "of Eid",
);

const heroBadge = computed(() =>
  isFirstDayOfEid.value
    ? "1 Shawwal • Eid ul-Fitr"
    : `${shawwalDay.value} Shawwal • Eid ul-Fitr`,
);

const heroBlurb = computed(() =>
  isFirstDayOfEid.value
    ? "May this blessed occasion bring you peace, prosperity, and endless joy."
    : `Today is the ${dayOrdinal.value} day of Eid in Pakistan. May Allah accept your Ramadan and keep the joy of Eid in your home.`,
);

const heroSupport = computed(() =>
  isFirstDayOfEid.value
    ? "Wishing you and your loved ones a beautiful Eid filled with happiness and blessings."
    : "Spend the day with gratitude, family, duas, and the spirit that Ramadan left behind.",
);

useSeoMeta({
  title: () => pageTitle.value,
  description: () => pageDescription.value,
  ogTitle: () => `${pageTitle.value} | Rozay.today`,
  ogDescription: () => pageDescription.value,
  twitterTitle: () => `${pageTitle.value} | Rozay.today`,
  twitterDescription: () => pageDescription.value,
});

const stars = createStyles(STAR_COUNT, (index) => {
  const size = 1 + seededValue(index + 11) * 2;
  const duration = 2 + seededValue(index + 17) * 3;
  const delay = seededValue(index + 23) * 3;

  return {
    left: `${seededValue(index + 31) * 100}%`,
    top: `${seededValue(index + 37) * 100}%`,
    width: `${size.toFixed(2)}px`,
    height: `${size.toFixed(2)}px`,
    animationDuration: `${duration.toFixed(2)}s`,
    animationDelay: `${delay.toFixed(2)}s`,
  };
});

const lanterns = createStyles(LANTERN_COUNT, (index) => {
  const left = 10 + seededValue(index + 41) * 80;
  const duration = 8 + seededValue(index + 47) * 4;
  const delay = seededValue(index + 53) * 4;

  return {
    left: `${left.toFixed(2)}%`,
    animationDuration: `${duration.toFixed(2)}s`,
    animationDelay: `${delay.toFixed(2)}s`,
  };
});

const particles = createStyles(PARTICLE_COUNT, (index) => {
  const size = 2 + seededValue(index + 59) * 4;
  const duration = 10 + seededValue(index + 67) * 10;
  const delay = seededValue(index + 71) * 5;

  return {
    left: `${(seededValue(index + 73) * 100).toFixed(2)}%`,
    width: `${size.toFixed(2)}px`,
    height: `${size.toFixed(2)}px`,
    animationDuration: `${duration.toFixed(2)}s`,
    animationDelay: `${delay.toFixed(2)}s`,
  };
});
</script>

<template>
  <div class="eid-container">
    <div class="stars-container" aria-hidden="true">
      <div
        v-for="star in stars"
        :key="star.key"
        class="star"
        :style="star.style"
      />
    </div>

    <div class="moon-container" aria-hidden="true">
      <div class="moon">
        <div class="moon-glow" />
        <div class="moon-crater crater-1" />
        <div class="moon-crater crater-2" />
        <div class="moon-crater crater-3" />
      </div>
    </div>

    <div class="lanterns" aria-hidden="true">
      <div
        v-for="lantern in lanterns"
        :key="lantern.key"
        class="lantern"
        :style="lantern.style"
      >
        <div class="lantern-top" />
        <div class="lantern-body" />
        <div class="lantern-bottom" />
        <div class="lantern-light" />
      </div>
    </div>

    <div class="content">
      <div class="greeting-wrapper">
        <div class="hero-badge">{{ heroBadge }}</div>

        <div class="pattern-border pattern-top" aria-hidden="true">
          <svg viewBox="0 0 100 10" preserveAspectRatio="none">
            <path
              d="M0,5 Q12.5,0 25,5 T50,5 T75,5 T100,5"
              stroke="currentColor"
              fill="none"
              stroke-width="0.5"
            />
            <path
              d="M0,5 Q12.5,10 25,5 T50,5 T75,5 T100,5"
              stroke="currentColor"
              fill="none"
              stroke-width="0.5"
            />
          </svg>
        </div>

        <h1 class="eid-title">
          <span class="title-line">{{ heroTitleTop }}</span>
          <span class="title-line">{{ heroTitleBottom }}</span>
        </h1>

        <p class="arabic-text" lang="ar" dir="rtl">عيد مبارك</p>

        <p class="blessing">
          {{ heroBlurb }}
          <br />
          {{ heroSupport }}
        </p>

        <div class="actions">
          <NuxtLink to="/calendar/" class="action-button action-button-primary">
            Ramadan Calendar
          </NuxtLink>
          <NuxtLink to="/dua/" class="action-button action-button-secondary">
            Duas & Guidance
          </NuxtLink>
        </div>

        <div class="pattern-border pattern-bottom" aria-hidden="true">
          <svg viewBox="0 0 100 10" preserveAspectRatio="none">
            <path
              d="M0,5 Q12.5,0 25,5 T50,5 T75,5 T100,5"
              stroke="currentColor"
              fill="none"
              stroke-width="0.5"
            />
            <path
              d="M0,5 Q12.5,10 25,5 T50,5 T75,5 T100,5"
              stroke="currentColor"
              fill="none"
              stroke-width="0.5"
            />
          </svg>
        </div>

        <div class="mosque-silhouette" aria-hidden="true">
          <svg viewBox="0 0 300 120" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="150" cy="60" rx="45" ry="40" fill="currentColor" opacity="0.3" />
            <ellipse cx="90" cy="75" rx="25" ry="22" fill="currentColor" opacity="0.25" />
            <ellipse cx="210" cy="75" rx="25" ry="22" fill="currentColor" opacity="0.25" />
            <rect x="40" y="50" width="12" height="70" fill="currentColor" opacity="0.3" />
            <rect x="248" y="50" width="12" height="70" fill="currentColor" opacity="0.3" />
            <circle cx="46" cy="45" r="8" fill="currentColor" opacity="0.3" />
            <circle cx="254" cy="45" r="8" fill="currentColor" opacity="0.3" />
            <polygon points="150,20 145,60 155,60" fill="currentColor" opacity="0.3" />
            <circle cx="150" cy="18" r="6" fill="currentColor" opacity="0.4" />
            <rect x="70" y="85" width="160" height="35" fill="currentColor" opacity="0.2" />
          </svg>
        </div>

        <div class="branding">
          <div class="brand-name">rozay.today</div>
          <div class="tagline">Your Ramadan Companion</div>
        </div>
      </div>
    </div>

    <div class="particles" aria-hidden="true">
      <div
        v-for="particle in particles"
        :key="particle.key"
        class="particle"
        :style="particle.style"
      />
    </div>
  </div>
</template>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&family=Cinzel:wght@400;600;700&family=Cormorant+Garamond:wght@300;400;500&display=swap");

.eid-container {
  position: relative;
  min-height: calc(100vh - 4.5rem);
  width: 100%;
  overflow: hidden;
  background:
    radial-gradient(circle at top, rgba(212, 175, 55, 0.16), transparent 28%),
    linear-gradient(135deg, #0a1128 0%, #1a1f3a 25%, #2a2d4a 50%, #1e2139 75%, #0d1b2a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.stars-container,
.lanterns,
.particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.star {
  position: absolute;
  background: #fff;
  border-radius: 999px;
  animation: twinkle linear infinite;
}

.moon-container {
  position: absolute;
  top: 8%;
  right: 12%;
  animation: float 6s ease-in-out infinite;
}

.moon {
  position: relative;
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #ffd89b 0%, #f9e4b7 100%);
  border-radius: 50%;
  box-shadow:
    0 0 40px rgba(255, 216, 155, 0.6),
    0 0 80px rgba(255, 216, 155, 0.3),
    inset -10px -10px 20px rgba(0, 0, 0, 0.1);
}

.moon-glow {
  position: absolute;
  inset: -20px;
  background: radial-gradient(circle, rgba(255, 216, 155, 0.3) 0%, transparent 70%);
  border-radius: 50%;
  animation: glow 3s ease-in-out infinite;
}

.moon-crater {
  position: absolute;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 50%;
}

.crater-1 {
  width: 20px;
  height: 20px;
  top: 30%;
  left: 25%;
}

.crater-2 {
  width: 15px;
  height: 15px;
  top: 55%;
  left: 50%;
}

.crater-3 {
  width: 12px;
  height: 12px;
  top: 40%;
  right: 20%;
}

.lantern {
  position: absolute;
  top: -100px;
  animation: float-down linear infinite;
}

.lantern-top {
  width: 20px;
  height: 6px;
  background: #d4af37;
  margin: 0 auto;
  border-radius: 3px 3px 0 0;
}

.lantern-body {
  position: relative;
  width: 30px;
  height: 40px;
  background: linear-gradient(to bottom, #ff6b6b 0%, #ee5a6f 50%, #c44569 100%);
  margin: 0 auto;
  border-radius: 0 0 15px 15px;
  box-shadow: 0 0 20px rgba(255, 107, 107, 0.6);
}

.lantern-bottom {
  width: 10px;
  height: 10px;
  background: #d4af37;
  margin: 0 auto;
  border-radius: 50%;
}

.lantern-light {
  position: absolute;
  top: 6px;
  left: 2px;
  width: 26px;
  height: 36px;
  background: radial-gradient(circle, rgba(255, 255, 200, 0.8) 0%, transparent 70%);
  border-radius: 0 0 13px 13px;
  animation: flicker 2s ease-in-out infinite;
}

.content {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 920px;
  text-align: center;
}

.greeting-wrapper {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 30px;
  padding: 4rem 3rem;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  animation: fadeInUp 1.2s ease-out;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  padding: 0.55rem 1rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(255, 255, 255, 0.07);
}

.pattern-border {
  width: 100%;
  height: 20px;
  margin: 2rem 0;
  color: rgba(212, 175, 55, 0.4);
}

.pattern-top {
  margin-bottom: 3rem;
}

.pattern-bottom {
  margin-top: 3rem;
  margin-bottom: 2rem;
}

.eid-title {
  margin: 0;
  font-family: "Cinzel", serif;
  font-size: clamp(4rem, 12vw, 8rem);
  font-weight: 700;
  line-height: 0.9;
  letter-spacing: 0.05em;
  background: linear-gradient(135deg, #ffd89b 0%, #d4af37 25%, #f9e4b7 50%, #d4af37 75%, #ffd89b 100%);
  background-size: 200% 200%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 4px 20px rgba(212, 175, 55, 0.5));
  animation: shimmer 3s ease-in-out infinite, title-entry 1s ease-out;
}

.title-line {
  display: block;
  animation: fadeInSlide 1s ease-out backwards;
}

.title-line:first-child {
  animation-delay: 0.2s;
}

.title-line:last-child {
  animation-delay: 0.4s;
}

.arabic-text {
  margin: 2rem 0;
  font-family: "Amiri", serif;
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: 700;
  color: rgba(255, 255, 255, 0.92);
  text-shadow: 0 2px 20px rgba(212, 175, 55, 0.4);
  animation: fadeInSlide 1s ease-out 0.6s backwards;
}

.blessing {
  max-width: 600px;
  margin: 2rem auto;
  font-family: "Cormorant Garamond", serif;
  font-size: clamp(1.1rem, 2.5vw, 1.5rem);
  line-height: 1.8;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.85);
  animation: fadeInSlide 1s ease-out 0.8s backwards;
}

.actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 2rem;
  animation: fadeInSlide 1s ease-out 0.9s backwards;
}

.action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.75rem;
  padding: 0.85rem 1.4rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 600;
  transition:
    transform 0.2s ease,
    background-color 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease;
}

.action-button:hover {
  transform: translateY(-1px);
}

.action-button-primary {
  background: linear-gradient(135deg, #d4af37 0%, #f6d78b 100%);
  color: #101828;
}

.action-button-secondary {
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.92);
}

.mosque-silhouette {
  max-width: 300px;
  margin: 3rem auto 2rem;
  color: rgba(212, 175, 55, 0.6);
  opacity: 0.6;
  animation: fadeInSlide 1s ease-out 1s backwards;
}

.mosque-silhouette svg {
  width: 100%;
  height: auto;
  filter: drop-shadow(0 4px 20px rgba(212, 175, 55, 0.3));
}

.branding {
  margin-top: 2rem;
  animation: fadeInSlide 1s ease-out 1.2s backwards;
}

.brand-name {
  margin-bottom: 0.5rem;
  font-family: "Cinzel", serif;
  font-size: clamp(1.2rem, 3vw, 1.8rem);
  font-weight: 600;
  letter-spacing: 0.1em;
  background: linear-gradient(135deg, #d4af37 0%, #ffd89b 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.tagline {
  font-family: "Cormorant Garamond", serif;
  font-size: clamp(0.9rem, 2vw, 1.1rem);
  font-weight: 300;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
}

.particle {
  position: absolute;
  bottom: -10px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(212, 175, 55, 0.8) 0%, transparent 70%);
  animation: rise linear infinite;
}

@keyframes twinkle {
  0%,
  100% {
    opacity: 0.2;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.3);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
}

@keyframes glow {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

@keyframes float-down {
  0% {
    transform: translateY(0) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(100vh) rotate(360deg);
    opacity: 0;
  }
}

@keyframes flicker {
  0%,
  100% {
    opacity: 0.8;
  }
  50% {
    opacity: 1;
  }
}

@keyframes rise {
  0% {
    transform: translateY(0) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(-100vh) rotate(360deg);
    opacity: 0;
  }
}

@keyframes shimmer {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInSlide {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes title-entry {
  from {
    transform: scale(0.9);
    filter: blur(10px);
  }
  to {
    transform: scale(1);
    filter: blur(0);
  }
}

@media (max-width: 768px) {
  .greeting-wrapper {
    padding: 3rem 2rem;
  }

  .moon-container {
    top: 5%;
    right: 5%;
  }

  .moon {
    width: 80px;
    height: 80px;
  }

  .blessing {
    font-size: 1.1rem;
  }
}

@media (max-width: 480px) {
  .eid-container {
    padding: 1rem;
  }

  .greeting-wrapper {
    padding: 2rem 1.5rem;
  }

  .actions {
    flex-direction: column;
  }

  .action-button {
    width: 100%;
  }
}
</style>
